import log from "loglevel";
import { useCallback, useMemo } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { SessionRequestPendingIndicator } from "../../components";
import { DeviceNotSupportingEncryptedVrStreamingError } from "../../components/DeviceNotSupportingEncryptedVrStreamingError";
import { RegionNotAvailableError } from "../../components/RegionNotAvailableError";
import { RegionNotSupportedWarning } from "../../components/RegionNotSupportedWarning";
import { useRegionNotSupportedWarning } from "../../hooks/useRegionNotSupportedWarning";
import { SessionType } from "../../session/types";
import { requestSession } from "../../signalR";
import { doesSupportEncryptedVrStreaming } from "../../utils/devices";
import { routes } from "../../utils/routes";
import { useLaunchPageContext } from "../LaunchPageContext";

export function CloudLaunchStandalone() {
  const navigate = useNavigate();

  const {
    application,
    applicationBuildId,
    organization,
    launchArgs,
    forceColdVm,
    encryptVrStream,
    useXrGateway,
    virtualMachineId,
    debugModeEnabled,
    vmImage,
    vmSize,
    renderRegion,
    targetDevice,
    streamer,
    useFixedIp,
  } = useLaunchPageContext();

  const encryptionUnsupportedOnTargetDevice = useMemo(
    () =>
      targetDevice &&
      encryptVrStream &&
      doesSupportEncryptedVrStreaming(targetDevice) === false,
    [encryptVrStream, targetDevice],
  );

  const navigateBack = useCallback(
    () =>
      navigate(
        generatePath(routes.application.details, {
          id: application.id,
        }),
      ),
    [application.id, navigate],
  );

  const { noValidRegion, alertProps } = useRegionNotSupportedWarning({
    applicationBuildId: applicationBuildId,
    requestedVmSize: vmSize,
    requestedRegion: renderRegion,
    xrGatewayRequired: useXrGateway,
    streamingGatewayRequired: streamer === "dcv",
    fixedIpRequired: useFixedIp,
    onRejected: navigateBack,
    onAccepted: useCallback(
      (selectedRegion) => {
        // launching in cloud with target hmd
        if (!targetDevice) {
          log.warn("No target device");
          return;
        }

        // ensure target device supports encryption if required
        if (encryptionUnsupportedOnTargetDevice) {
          log.warn("Target device does not support encryption");
          return;
        }

        if (!selectedRegion?.region?.name) {
          return;
        }

        requestSession({
          appId: applicationBuildId,
          type: SessionType.CloudRenderedVR,
          deviceIdentifier: targetDevice.identifier,
          launchArguments: launchArgs,
          organizationId: organization.id,
          renderRegion: selectedRegion.region?.name,
          vmSize: selectedRegion.vmSize,
          forceColdVm,
          encryptVrStream,
          vmImage,
          virtualMachineId,
          debugModeEnabled,
          // to enable DCV streaming gateway for browser sessions
          withStreamingGateway: streamer === "dcv",
          // to enable XR streaming gateway for VR sessions
          withXrGateway:
            // FIXME: we don't get this information yet for the region so we assume it's working if not provided, otherwise
            (selectedRegion.region.supportsXRGateway === undefined
              ? true
              : selectedRegion.region.supportsXRGateway) && useXrGateway,
        })
          .then(() => log.debug("Starting VR streaming session"))
          .catch(log.error);
      },
      [
        applicationBuildId,
        debugModeEnabled,
        encryptVrStream,
        useXrGateway,
        encryptionUnsupportedOnTargetDevice,
        forceColdVm,
        launchArgs,
        organization.id,
        targetDevice,
        streamer,
        virtualMachineId,
        vmImage,
      ],
    ),
  });

  return (
    <>
      {!noValidRegion && !encryptionUnsupportedOnTargetDevice && (
        <SessionRequestPendingIndicator />
      )}
      <RegionNotSupportedWarning
        applicationId={application.id}
        {...alertProps}
      />
      <RegionNotAvailableError isOpen={noValidRegion} onClose={navigateBack} />
      <DeviceNotSupportingEncryptedVrStreamingError
        isOpen={encryptionUnsupportedOnTargetDevice ?? false}
        onClose={navigateBack}
      />
    </>
  );
}
